import { PageHeader, DataTable } from '../components/AdminPageUI';
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

export default function TransportBusesPage() {
  const [buses, setBuses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', capacity: '', driverName: '', driverPhone: '', teacher: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [busRes, teacherRes] = await Promise.all([
        api.get('/transport/buses'),
        api.get('/users/all') // Assuming we get all users and filter by teacher, or /teachers
      ]);
      setBuses(busRes.data.buses || []);
      
      const allUsers = teacherRes.data.users || teacherRes.data || [];
      setTeachers(allUsers.filter(u => u.role === 'teacher'));
    } catch (err) {
      toast.error('Failed to load buses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (bus = null) => {
    if (bus) {
      setIsEditing(true);
      setCurrentId(bus._id);
      setFormData({
        name: bus.name,
        capacity: bus.capacity,
        driverName: bus.driverName || '',
        driverPhone: bus.driverPhone || '',
        teacher: bus.teacher?._id || ''
      });
    } else {
      setIsEditing(false);
      setCurrentId(null);
      setFormData({ name: '', capacity: '', driverName: '', driverPhone: '', teacher: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.capacity || !formData.driverName) {
      toast.warning('Please fill all required fields');
      return;
    }
    try {
      if (isEditing) {
        await api.put(`/transport/buses/${currentId}`, formData);
        toast.success('Bus updated successfully');
      } else {
        await api.post('/transport/buses', formData);
        toast.success('Bus added successfully');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save bus');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bus?')) return;
    try {
      await api.delete(`/transport/buses/${id}`);
      toast.success('Bus deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete bus');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title={<>Transport Buses</>} description={<>Manage school buses, capacity, and vehicle details.</>} actions={<>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                Add Bus
              </Button>
      </>}></PageHeader>

      {loading ? (
        <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 5 }} />
      ) : (
        <Card elevation={2}>
          <CardContent>
            <DataTable component={Paper} elevation={0}>
              <Table>
                <TableHead sx={{ bgcolor: 'background.default' }}>
                  <TableRow>
                    <TableCell>Bus Name</TableCell>
                    <TableCell>Capacity</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Driver Phone</TableCell>
                    <TableCell>Assigned Teacher</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {buses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No buses found. Add one to get started.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    buses.map(bus => (
                      <TableRow key={bus._id} hover>
                        <TableCell fontWeight="bold">{bus.name}</TableCell>
                        <TableCell>{bus.capacity}</TableCell>
                        <TableCell>{bus.driverName}</TableCell>
                        <TableCell>{bus.driverPhone || 'N/A'}</TableCell>
                        <TableCell>{bus.teacher?.name || 'Not assigned'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="primary" onClick={() => handleOpenDialog(bus)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(bus._id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </DataTable>
          </CardContent>
        </Card>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Bus' : 'Add New Bus'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField 
              label="Bus Name / Plate" 
              fullWidth 
              required
              value={formData.name} 
              onChange={e => setFormData({ ...formData, name: e.target.value })} 
            />
            <TextField 
              label="Capacity (Seats)" 
              type="number" 
              fullWidth 
              required
              value={formData.capacity} 
              onChange={e => setFormData({ ...formData, capacity: e.target.value })} 
            />
            <TextField 
              label="Driver Name" 
              fullWidth 
              required
              value={formData.driverName} 
              onChange={e => setFormData({ ...formData, driverName: e.target.value })} 
            />
            <TextField 
              label="Driver Phone" 
              fullWidth 
              value={formData.driverPhone} 
              onChange={e => setFormData({ ...formData, driverPhone: e.target.value })} 
            />
            <TextField 
              select 
              label="Assigned Teacher (In-Charge)" 
              fullWidth
              value={formData.teacher} 
              onChange={e => setFormData({ ...formData, teacher: e.target.value })} 
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {teachers.map(t => (
                <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
